import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import FilterPanel from './FilterPanel';
import BrandLogo from './BrandLogo';
import { Button } from './ui/button';
import { useT } from '../services/i18n';
import { catName, subName } from '../services/catalogI18n';
import { ENERGIES, productFuelOk, energyLabel } from '../services/fuel';

export const EMPTY_SEARCH_FILTERS = { category: '', subcategory: '', brands: [], fuel: '', price: { min: null, max: null } };

export const applySearchFilters = (products, f) => products.filter(p =>
  (!f.category || p.category === f.category) &&
  (!f.subcategory || p.subcategory === f.subcategory) &&
  (!f.brands.length || f.brands.includes(p.brand)) &&
  productFuelOk(p, f.fuel) &&
  (f.price.min === null || p.price >= f.price.min) &&
  (f.price.max === null || p.price <= f.price.max));

export const countActiveSearchFilters = (f) => (f.category ? 1 : 0) + (f.subcategory ? 1 : 0) + f.brands.length + (f.fuel ? 1 : 0) + (f.price.min !== null || f.price.max !== null ? 1 : 0);

// Filters live in the URL (?cat=&sous=&marques=a|b&energie=&pmin=&pmax=) so they survive back/forward and can be shared
const FILTER_KEYS = ['cat', 'sous', 'marques', 'energie', 'pmin', 'pmax'];
const num = (v) => (v === null || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
export const useSearchFilterParams = (searchParams, setSearchParams) => {
  const filters = {
    category: searchParams.get('cat') || '',
    subcategory: searchParams.get('sous') || '',
    brands: (searchParams.get('marques') || '').split('|').filter(Boolean),
    fuel: ENERGIES.includes(searchParams.get('energie')) ? searchParams.get('energie') : '',
    price: { min: num(searchParams.get('pmin')), max: num(searchParams.get('pmax')) },
  };
  const setFilters = (f) => {
    const next = new URLSearchParams(searchParams);
    FILTER_KEYS.forEach(k => next.delete(k));
    if (f.category) next.set('cat', f.category);
    if (f.subcategory) next.set('sous', f.subcategory);
    if (f.brands.length) next.set('marques', f.brands.join('|'));
    if (f.fuel) next.set('energie', f.fuel);
    if (f.price.min !== null) next.set('pmin', String(f.price.min));
    if (f.price.max !== null) next.set('pmax', String(f.price.max));
    setSearchParams(next);
  };
  return [filters, setFilters];
};

const countBy = (list, key) => list.reduce((acc, p) => { const v = p[key]; if (v) acc[v] = (acc[v] || 0) + 1; return acc; }, {});

// Left-hand filters of the search results page: category › subcategory, brand, energy, price
export const SearchFilters = ({ products, filters, onChange, categories, resultCount, hideBrand = false }) => {
  const { t, lang } = useT();
  const [priceInput, setPriceInput] = useState({ min: filters.price.min ?? '', max: filters.price.max ?? '' });
  useEffect(() => { setPriceInput({ min: filters.price.min ?? '', max: filters.price.max ?? '' }); }, [filters.price.min, filters.price.max]);
  const set = (patch) => onChange({ ...filters, ...patch });
  const reset = () => { setPriceInput({ min: '', max: '' }); onChange(EMPTY_SEARCH_FILTERS); };
  const active = countActiveSearchFilters(filters);
  const catCounts = countBy(products, 'category');
  const inCat = filters.category ? products.filter(p => p.category === filters.category) : products;
  const subCounts = countBy(inCat, 'subcategory');
  const brandCounts = countBy(applySearchFilters(products, { ...filters, brands: [] }), 'brand');
  const fuelCounts = ENERGIES.reduce((acc, e) => { acc[e] = applySearchFilters(products, { ...filters, fuel: e }).length; return acc; }, {});
  const catObj = (slug) => categories.find(c => c.slug === slug);
  const applyPrice = () => set({ price: { min: priceInput.min === '' ? null : Number(priceInput.min), max: priceInput.max === '' ? null : Number(priceInput.max) } });
  return (
    <FilterPanel resultCount={resultCount} onReset={reset} activeCount={active}>
      <div className="bg-white rounded-lg shadow-sm p-4" data-testid="search-filters">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">{t('cat.filterBy')}</h3>
          {active > 0 && <button data-testid="search-filters-reset" onClick={reset} className="text-xs text-blue-600 hover:underline">{t('cat.resetFilters')}</button>}
        </div>
        <div className="space-y-5">
          <div data-testid="search-filter-category">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{t('search.filterCategory')}</h4>
            <ul className="space-y-0.5">
              {Object.entries(catCounts).sort((a, b) => b[1] - a[1]).map(([slug, n]) => {
                const open = filters.category === slug;
                return (
                  <li key={slug}>
                    <button type="button" data-testid={`search-filter-cat-${slug}`} aria-pressed={open} onClick={() => set({ category: open ? '' : slug, subcategory: '' })}
                      className={`w-full flex items-center gap-1.5 text-left text-sm rounded px-1.5 py-1 ${open ? 'bg-[#1e3a5f] text-white' : 'text-gray-700 hover:bg-gray-50'}`}>
                      {open ? <ChevronDown size={14} /> : <ChevronRight size={14} className="text-gray-400" />}
                      <span className="flex-1 truncate">{catName(catObj(slug), lang) || slug}</span>
                      <span className={`text-xs ${open ? 'text-white/70' : 'text-gray-400'}`}>{n}</span>
                    </button>
                    {open && (
                      <ul className="ml-5 mt-1 space-y-0.5" data-testid="search-filter-subcategories">
                        {Object.entries(subCounts).sort((a, b) => b[1] - a[1]).map(([sub, m]) => (
                          <li key={sub}>
                            <button type="button" data-testid={`search-filter-sub-${sub.replace(/[^\p{L}\p{N}]+/gu, '-')}`} aria-pressed={filters.subcategory === sub} onClick={() => set({ subcategory: filters.subcategory === sub ? '' : sub })}
                              className={`w-full flex items-center justify-between text-left text-xs rounded px-1.5 py-1 ${filters.subcategory === sub ? 'bg-orange-50 text-orange-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                              <span className="truncate">{subName(catObj(slug), sub, lang)}</span><span className="text-gray-400 ml-2">{m}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {!hideBrand && Object.keys(brandCounts).length > 1 && (
            <div data-testid="search-filter-brand">
              <h4 className="text-sm font-medium text-gray-700 mb-2">{t('cat.brand')}</h4>
              <div className="max-h-56 overflow-y-auto pr-1">
                {Object.entries(brandCounts).sort((a, b) => b[1] - a[1]).map(([brand, n]) => (
                  <label key={brand} className="flex items-center gap-2 py-1 cursor-pointer">
                    <input type="checkbox" data-testid={`search-filter-brand-${brand}`} checked={filters.brands.includes(brand)} onChange={() => set({ brands: filters.brands.includes(brand) ? filters.brands.filter(b => b !== brand) : [...filters.brands, brand] })} className="rounded border-gray-300 text-orange-500 focus:ring-orange-500" />
                    <span className="text-sm text-gray-600 flex-1 flex items-center gap-2 min-w-0"><BrandLogo brand={brand} size="xs" showName={false} testId={`search-filter-brand-logo-${brand}`} /><span className="truncate">{brand}</span></span>
                    <span className="text-xs text-gray-400">{n}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div data-testid="search-filter-fuel">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{t('fuelR.title')}</h4>
            <div className="flex flex-wrap gap-1.5">
              {['', ...ENERGIES].map(e => (
                <button key={e || 'all'} type="button" data-testid={`search-filter-fuel-${e || 'all'}`} aria-pressed={filters.fuel === e} onClick={() => set({ fuel: e })}
                  className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${filters.fuel === e ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'}`}>
                  {e ? energyLabel(t, e) : t('fuelR.all')}{e ? <span className={`ml-1 ${filters.fuel === e ? 'text-white/70' : 'text-gray-400'}`}>{fuelCounts[e]}</span> : null}
                </button>
              ))}
            </div>
          </div>

          <div data-testid="search-filter-price">
            <h4 className="text-sm font-medium text-gray-700 mb-2">{t('cat.price')}</h4>
            <div className="flex items-center gap-2">
              <input type="number" min="0" placeholder="Min" data-testid="search-filter-price-min" value={priceInput.min} onChange={(e) => setPriceInput(p => ({ ...p, min: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && applyPrice()} className="w-full px-2 py-1 border rounded text-sm" />
              <span className="text-gray-400">-</span>
              <input type="number" min="0" placeholder="Max" data-testid="search-filter-price-max" value={priceInput.max} onChange={(e) => setPriceInput(p => ({ ...p, max: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && applyPrice()} className="w-full px-2 py-1 border rounded text-sm" />
            </div>
            <Button data-testid="search-filters-apply" onClick={applyPrice} className="w-full mt-2 bg-orange-500 hover:bg-orange-600">{t('common.apply')}</Button>
          </div>
        </div>
      </div>
    </FilterPanel>
  );
};

export default SearchFilters;
